import React from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card'
import { Badge } from '../../ui/badge'
import { FileText, Calendar, Globe } from 'lucide-react'
import { EncryptedImageViewer } from './EncryptedImageViewer'

interface IdentityDocument {
  document_type: string
  document_number: string
  issue_date: string
  expiry_date?: string
  issuing_country: string
  issuing_authority?: string
  documents?: any
  document_images?: {
    front?: string
    back?: string
  }
}

interface KycIdentityDocumentsProps {
  documents: IdentityDocument[]
}

export const KycIdentityDocuments: React.FC<KycIdentityDocumentsProps> = ({ documents }) => {
  if (!documents || documents.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center space-x-2 text-base">
            <FileText className="h-4 w-4" />
            <span>Identity Documents</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-xs text-muted-foreground">No identity documents provided</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-3">
      {documents.map((document, index) => (
        <Card key={index}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2 text-base">
                <FileText className="h-4 w-4" />
                <span>Identity Document {documents.length > 1 ? `#${index + 1}` : ''}</span>
              </CardTitle>
              <Badge variant="outline" className="text-xs">{document.document_type}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 pt-0">
            {/* Document Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Document Number</label>
                <p className="text-sm font-mono">{document.document_number}</p>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground">Issuing Country</label>
                <p className="text-sm">{document.issuing_country}</p>
              </div>

              {document.issuing_authority && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Issuing Authority</label>
                  <p className="text-sm">{document.issuing_authority}</p>
                </div>
              )}

              <div className="flex items-start space-x-2">
                <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Issue Date</label>
                  <p className="text-sm">{document.issue_date}</p>
                </div>
              </div>

              {document.expiry_date && document.expiry_date.trim() !== '' && (
                <div className="flex items-start space-x-2">
                  <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Expiry Date</label>
                    <p className="text-sm">{document.expiry_date}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Document Images - Legacy Format */}
            {document.document_images && (
              <div className="pt-2 border-t">
                <h4 className="text-xs font-semibold mb-2">Document Images (Legacy)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {document.document_images.front && document.document_images.front.trim() !== '' && (
                    <EncryptedImageViewer
                      documentId={document.document_images.front}
                      label="Front Side"
                      mimeType="image/jpeg"
                    />
                  )}
                  {document.document_images.back && document.document_images.back.trim() !== '' && (
                    <EncryptedImageViewer
                      documentId={document.document_images.back}
                      label="Back Side"
                      mimeType="image/jpeg"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Note for New Format */}
            {!document.document_images && (
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground italic">
                  Document images are stored in vault documents. See the Vault Documents section below.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
